import { Box, Collapse, Grid, List, ListItem, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { Button } from '@chr/eds-react';
import { useTranslation } from '@chr/online-shared-ui-localizations';

import {
  NonPurchaseOrderItem,
} from '@/common/apis/dtos';
import { HazMatSvgIcon, TempSvgIcon } from '@/common/components/icons/Icons';
import { InformationDisplay } from '@/common/components/information-display/InformationDisplay';
import { ShowMoreText } from '@/common/components/show-more-text/ShowMoreText';
import { StyledTableCell, StyledTableRow } from '@/common/components/table';
import {
  formatNonPoItemProperties,
  mergeItemsIntoStrings,
  removeExtraSpace
} from '@/common/utilities/display-utilities';
import ControlledTooltip from '@/common/components/controlled-tooltip/ControlledTooltip';
import { useState } from 'react';
import { Dimensions } from './Dimensions';

export interface NonPoItemProps {
  item: NonPurchaseOrderItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const NonPoItem = ({ item, isExpanded, onToggleExpand }: NonPoItemProps) => {
  const translate = useTranslation();

  const {
    isHazmat,
    isTempControlled,
    outerPackQty,
    weight,
    volume,
    formattedDimensions,
    marks,
    notes,
    minTemp,
    maxTemp
  } = formatNonPoItemProperties(item);
  const [expandedDims, setExpandedDims] = useState<boolean>(false);
  
  const dimensionsStrings = mergeItemsIntoStrings(formattedDimensions);

  return (
    <>
      <StyledTableRow data-testid="cargo-details">
        <StyledTableCell align="left"  sx={{maxWidth: 200}}>
          <ControlledTooltip title={removeExtraSpace(item.poNumber, '-')}>
            <Typography component="p" variant="body1" noWrap>
              {removeExtraSpace(item.poNumber, '-')}
            </Typography>
          </ControlledTooltip>
        </StyledTableCell>
        <StyledTableCell align="left" sx={{maxWidth: 150}}>
          <ControlledTooltip title={removeExtraSpace(item.itemId, '-')}>
            <Typography component="p" noWrap>
              {removeExtraSpace(item.itemId, '-')}
            </Typography>
          </ControlledTooltip>
        </StyledTableCell>
        <StyledTableCell align="left">{removeExtraSpace(item.sku, '-')}</StyledTableCell>

        <StyledTableCell sx={{ padding: '12px 14px', maxWidth: 300 }}>
          <Box display="flex" alignItems={'center'}>
            {(isHazmat || isTempControlled) &&
              <Box display="flex" paddingRight="8px" gap="4px">
                {isHazmat && <HazMatSvgIcon />}
                {isTempControlled && <TempSvgIcon />}
              </Box>
            }          
            <ControlledTooltip title={removeExtraSpace(item.descriptionOfGoods, '-')}>
              <Typography component="p" variant="body1" noWrap>
                {removeExtraSpace(item.descriptionOfGoods, '-')}
              </Typography>
            </ControlledTooltip>            
          </Box>
        </StyledTableCell>

        <StyledTableCell align="right">{removeExtraSpace(outerPackQty, '-')}</StyledTableCell>
        <StyledTableCell align="right">{weight}</StyledTableCell>
        <StyledTableCell align="right">{volume}</StyledTableCell>

        <StyledTableCell align="left" sx={{ padding: '0 14px' }} className="no-print">
          <Box sx={{ minWidth: 150 }}>
            <Button
              startIcon={isExpanded ? 'expand_less' : 'expand_more'}
              onClick={onToggleExpand}
              variant={"tertiary"}
              buttonText={translate(isExpanded ? 'Hide' : 'View')}
            />
          </Box>
        </StyledTableCell>
      </StyledTableRow>
      <TableRow className="more-row" data-testid="cargo-details-more">
        <TableCell sx={{ padding: 0, border: 'none' }} colSpan={12}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ paddingX: 5, paddingTop: 3, paddingBottom: 3, backgroundColor: 'grey.50' }}>
              <Grid container spacing={3} data-testid="cargo-details">
                <Grid item md={3} sm={6} xs={12}>
                  <InformationDisplay label={translate('Item ID')} text={item.itemId ?? '-'} />
                </Grid>

                <Grid item md={3} sm={6} xs={12}>
                  <Dimensions
                    dimensionsStrings={dimensionsStrings}
                  />
                </Grid>

                <Grid item md={3} sm={6} xs={12}>
                  <Typography data-testid={'marks'} variant="body1">{translate('Marks')}</Typography>
                  <ShowMoreText variant="subtitle2" text={marks} />
                </Grid>

                <Grid item md={3} sm={6} xs={12}>
                  <Typography data-testid={'marks'} variant="body1">{translate('Commodity')}</Typography>
                  <ShowMoreText variant="subtitle2" text={notes} />
                </Grid>

                <Grid item md={3} sm={6} xs={12}>
                  <Stack direction={'row'} spacing={4}>
                    <InformationDisplay label={translate('Pallets')} text={item.pallets ?? '-'} />
                    <InformationDisplay
                      data-testid={'hazmat-value'}
                      label={translate('Hazmat')}
                      textVariant={'subtitle200'}
                      text={isHazmat ? translate('YES') : translate('NO')}
                    />
                  </Stack>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Box sx={{ display: 'inline-flex', gap: '4px' }}>
                    <Typography data-testid={'temp-control-label'} variant="body1">
                      {`${translate('Temperature controlled')}:`}
                    </Typography>

                    <Typography data-testid={'temp-control-value'} variant="subtitle200">
                      {isTempControlled ? translate('YES') : translate('NO')}
                    </Typography>
                  </Box>

                  <List sx={{ listStyleType: 'disc', paddingLeft: 5 }} dense={true} disablePadding={true}>
                    <ListItem sx={{ display: 'list-item', paddingBottom: 0, paddingLeft: 0 }}>{translate('Min')}: {minTemp}</ListItem>
                    <ListItem sx={{ display: 'list-item', paddingBottom: 0, paddingLeft: 0 }}>{translate('Max')}: {maxTemp}</ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
